import { FlexWrapper, Input, notify, Wrapper } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { useCatchEventCallback } from '@affine/core/components/hooks/use-catch-event-hook';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { Upload } from '@affine/core/components/pure/file-upload';
import { WorkspaceAvatar } from '@affine/core/components/workspace-avatar';
import { AuthService, ServerService } from '@affine/core/modules/cloud';
import { AvatarAssetCacheService } from '@affine/core/modules/cloud/services/avatar-asset-cache';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import {
  type Workspace,
  WorkspaceService,
} from '@affine/core/modules/workspace';
import { validateAndReduceImage } from '@affine/core/utils/reduce-image';
import { UNTITLED_WORKSPACE_NAME } from '@affine/env/constant';
import { useI18n } from '@affine/i18n';
import { CameraIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  AvatarUploadOverlay,
  useAvatarUploadTask,
} from '../../shared/avatar-upload-overlay';
import * as style from './style.css';

function getWorkspaceRootDocId(workspace: Workspace) {
  const rootDocId =
    workspace.rootYDoc.guid || workspace.id || workspace.meta.id;
  if (!rootDocId) {
    throw new Error('Workspace root document id is missing');
  }
  return rootDocId;
}

async function flushWorkspaceRootDoc(workspace: Workspace) {
  const docEngine = workspace.engine.doc;
  if (typeof docEngine.flushAndWaitForSynced !== 'function') {
    return;
  }
  await docEngine.flushAndWaitForSynced(getWorkspaceRootDocId(workspace));
}

export const ProfilePanel = () => {
  const t = useI18n();

  const workspace = useService(WorkspaceService).workspace;
  const avatarCache = useService(AvatarAssetCacheService);
  const authService = useService(AuthService);
  const server = useService(ServerService).server;
  const permissionService = useService(WorkspacePermissionService);
  const isOwner = useLiveData(permissionService.permission.isOwner$);
  const account = useLiveData(authService.session.account$);
  useEffect(() => {
    permissionService.permission.revalidate();
  }, [permissionService]);

  const [name, setName] = useState('');
  const currentName = useLiveData(workspace.name$);
  const workspaceInfo = useWorkspaceInfo(workspace.meta);
  const { isAvatarUploading, runAvatarUploadTask } = useAvatarUploadTask();
  const profileName = workspaceInfo?.name;
  const hasProfileName =
    typeof profileName === 'string' && profileName.length > 0;
  const hasRootName =
    typeof currentName === 'string' &&
    currentName.length > 0 &&
    (currentName !== UNTITLED_WORKSPACE_NAME || !hasProfileName);
  const effectiveName =
    (hasRootName ? currentName : undefined) ??
    profileName ??
    UNTITLED_WORKSPACE_NAME;

  // Owners can always edit the workspace name, even if Y.js hasn't loaded yet.
  // The name will be written to Y.js when the user saves.
  const workspaceIsReady = true;

  useEffect(() => {
    setName(effectiveName);
  }, [effectiveName]);

  const setWorkspaceAvatar = useCallback(
    async (file: File | null) => {
      if (!workspace) {
        return;
      }
      if (!file) {
        workspace.setAvatar('');
        await flushWorkspaceRootDoc(workspace);
        return;
      }
      try {
        const reducedFile = await validateAndReduceImage(file);
        const blobs = workspace.docCollection.blobSync;
        const blobId = await blobs.set(reducedFile);
        if (account?.id) {
          await avatarCache
            .cacheBlob(
              {
                kind: 'workspace-logo',
                scope: {
                  accountId: account.id,
                  serverId: server.id,
                },
                entityId: workspace.id,
                source: blobId,
              },
              reducedFile
            )
            .catch(error => {
              console.warn('Failed to cache workspace avatar locally', error);
            });
        }
        workspace.setAvatar(blobId);
        await flushWorkspaceRootDoc(workspace);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [account?.id, avatarCache, server.id, workspace]
  );

  const setWorkspaceName = useCallback(
    (name: string) => {
      if (!workspace) {
        return;
      }
      workspace.setName(name);
    },
    [workspace]
  );

  const [input, setInput] = useState<string>('');
  useEffect(() => {
    setInput(name);
  }, [name]);

  const handleUpdateWorkspaceName = useCallback(
    (name: string) => {
      setWorkspaceName(name);
      notify.success({ title: t['Update workspace name success']() });
    },
    [setWorkspaceName, t]
  );

  const handleSetInput = useCallback((value: string) => {
    setInput(value);
  }, []);

  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.code === 'Enter' && name !== input) {
        handleUpdateWorkspaceName(input);
      }
    },
    [handleUpdateWorkspaceName, input, name]
  );

  const handleClick = useCallback(() => {
    handleUpdateWorkspaceName(input);
  }, [handleUpdateWorkspaceName, input]);

  const handleRemoveUserAvatar = useCatchEventCallback(async () => {
    await runAvatarUploadTask(async () => {
      await setWorkspaceAvatar(null);
    });
  }, [runAvatarUploadTask, setWorkspaceAvatar]);

  const handleUploadAvatar = useCallback(
    (file: File) => {
      void runAvatarUploadTask(async () => {
        try {
          await setWorkspaceAvatar(file);
          notify.success({ title: 'Update workspace avatar success' });
        } catch (error) {
          notify.error({
            title: 'Update workspace avatar failed',
            message: error instanceof Error ? error.message : String(error),
          });
        }
      });
    },
    [runAvatarUploadTask, setWorkspaceAvatar]
  );

  const canAdjustAvatar = workspaceIsReady && isOwner;

  return (
    <div className={style.profileWrapper}>
      <Upload
        accept="image/gif,image/jpeg,image/jpg,image/png,image/svg"
        fileChange={handleUploadAvatar}
        data-testid="upload-avatar"
        disabled={!isOwner || isAvatarUploading}
      >
        <AvatarUploadOverlay uploading={isAvatarUploading} rounded={8}>
          <WorkspaceAvatar
            meta={workspace.meta}
            size={56}
            name={name}
            rounded={8}
            colorfulFallback
            hoverIcon={
              !isAvatarUploading && isOwner ? <CameraIcon /> : undefined
            }
            onRemove={
              canAdjustAvatar && !isAvatarUploading
                ? handleRemoveUserAvatar
                : undefined
            }
            avatarTooltipOptions={
              canAdjustAvatar
                ? { content: t['Click to replace photo']() }
                : undefined
            }
            removeTooltipOptions={
              canAdjustAvatar ? { content: t['Remove photo']() } : undefined
            }
            data-testid="workspace-setting-avatar"
            removeButtonProps={{
              ['data-testid' as string]:
                'workspace-setting-remove-avatar-button',
            }}
          />
        </AvatarUploadOverlay>
      </Upload>

      <Wrapper marginLeft={20}>
        <div className={style.label}>{t['Workspace Name']()}</div>
        <FlexWrapper alignItems="center" flexGrow="1">
          <Input
            disabled={!workspaceIsReady || !isOwner}
            value={input}
            style={{ width: 280, height: 32 }}
            data-testid="workspace-name-input"
            placeholder={t['Workspace Name']()}
            maxLength={64}
            minLength={0}
            onChange={handleSetInput}
            onKeyUp={handleKeyUp}
          />
          {input === name ? null : (
            <Button
              data-testid="save-workspace-name"
              onClick={handleClick}
              style={{
                marginLeft: '12px',
              }}
            >
              {t['com.streamlain.editCollection.save']()}
            </Button>
          )}
        </FlexWrapper>
      </Wrapper>
    </div>
  );
};
